import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Salary() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const canManage = ["Admin", "Accounts"].includes(user?.role);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/salary/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data.preview);
      toast.success(`Parsed ${r.data.preview.length} rows`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    try {
      const okRows = preview.filter(p => p.status === "ok");
      const r = await api.post("/salary/commit", okRows);
      toast.success(`Posted ${r.data.posted} salary entries to Personnel`);
      setPreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const okCount = preview?.filter(p => p.status === "ok").length ?? 0;
  const flagCount = preview?.filter(p => p.status !== "ok").length ?? 0;

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Payroll Integration</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Salary Cost Import</h1>
        <p className="text-sm text-slate-500 mt-1">Import the monthly salary file (CSV). Amounts are allocated across projects and cost centres by mapped %, posted to Personnel head.</p>
      </div>

      {canManage && (
        <Card className="mb-6">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-semibold">Step 1 — Upload CSV</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">Columns: employee_code, month (YYYY-MM), gross, deductions, net</div>
              </div>
              <div>
                <input type="file" ref={fileRef} accept=".csv" onChange={upload} className="hidden" data-testid="salary-csv-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={loading} className="bg-[#0F172A]" data-testid="salary-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> {loading ? "Parsing…" : "Upload Salary CSV"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="font-semibold">Step 2 — Review Allocation Preview</div>
              <div className="flex gap-2 items-center">
                <Badge variant="outline" className="text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" />{okCount} ready</Badge>
                {flagCount > 0 && <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />{flagCount} flagged</Badge>}
                <Button onClick={commit} disabled={okCount === 0} className="bg-emerald-700" data-testid="salary-commit-btn">Commit {okCount} Postings</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2">Emp Code</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Month</th>
                    <th className="text-right px-3 py-2">Gross</th>
                    <th className="text-right px-3 py-2">Deductions</th>
                    <th className="text-right px-3 py-2">Net</th>
                    <th className="text-left px-3 py-2">Alloc Total</th>
                    <th className="text-left px-3 py-2">Splits</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${r.status !== "ok" ? "bg-red-50" : ""}`} data-testid={`salary-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">{r.employee_code}</td>
                      <td className="px-3 py-2">{r.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.month}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{r.gross?.toLocaleString() ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{r.deductions?.toLocaleString() ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">₹{r.net?.toLocaleString() ?? "—"}</td>
                      <td className="px-3 py-2 font-mono">{r.total_pct ?? 0}%</td>
                      <td className="px-3 py-2 text-xs">{r.splits?.length || 0} splits</td>
                      <td className="px-3 py-2">
                        {r.status === "ok" ? <Badge className="bg-emerald-600">OK</Badge> :
                         r.status === "flagged" ? <Badge variant="destructive">Alloc ≠ 100%</Badge> :
                         <Badge variant="destructive">Unknown Emp</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
